/**
 * Dropdown chọn tỉnh/thành — variant inline (panel) hoặc sheet (select native).
 *
 * Đóng panel khi click bên ngoài; tùy chọn "Tất cả" reset giá trị rỗng.
 */
import { useEffect, useRef } from 'react';
import { AnimatedPanel } from './AnimatedPanel';

const CheckIcon = () => (
  <svg className="category-dropdown-check" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="category-dropdown-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Dropdown chọn tỉnh/thành.
 * @param {Object} props
 * @param {Array} [props.provinces]
 * @param {string} [props.value]
 * @param {Function} props.onChange
 * @param {string} props.label
 * @param {string} props.allLabel
 * @param {string} props.placeholder
 * @param {string} [props.closeLabel]
 * @param {'inline'|'sheet'} [props.variant]
 * @param {boolean} props.isOpen
 * @param {Function} [props.onOpenChange]
 */
export const ProvinceDropdown = ({
  provinces = [],
  value = '',
  onChange,
  label,
  allLabel,
  placeholder,
  closeLabel = 'Đóng',
  variant = 'inline',
  isOpen,
  onOpenChange,
}) => {
  const rootRef = useRef(null);
  const displayValue = value || placeholder;

  const handleSelect = (provinceName) => {
    onChange(provinceName);
    onOpenChange?.(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDownOutside);

    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside);
    };
  }, [isOpen, onOpenChange]);

  const options = (
    <div className="category-dropdown-list province-dropdown-list" role="listbox" aria-label={label}>
      <button
        type="button"
        role="option"
        aria-selected={!value}
        className={`category-dropdown-option ${!value ? 'is-active' : ''}`}
        onClick={() => handleSelect('')}
      >
        <span className="category-dropdown-icon" aria-hidden="true">
          🇻🇳
        </span>
        <span className="category-dropdown-copy">
          <strong>{allLabel}</strong>
        </span>
        {!value && <CheckIcon />}
      </button>

      {provinces.map((province) => {
        const isActive = province.name === value;

        return (
          <button
            key={province.id}
            type="button"
            role="option"
            aria-selected={isActive}
            className={`category-dropdown-option ${isActive ? 'is-active' : ''}`}
            onClick={() => handleSelect(province.name)}
          >
            <span className="category-dropdown-icon" aria-hidden="true">
              📍
            </span>
            <span className="category-dropdown-copy">
              <strong>{province.name}</strong>
            </span>
            {isActive && <CheckIcon />}
          </button>
        );
      })}
    </div>
  );

  if (variant === 'sheet') {
    return (
      <select
        className="dropdown-sheet-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
      >
        <option value="">{allLabel}</option>
        {provinces.map((province) => (
          <option key={province.id} value={province.name}>
            {province.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`category-dropdown province-dropdown category-dropdown--inline ${isOpen ? 'is-open' : ''}`.trim()}
    >
      <button
        type="button"
        className={`category-dropdown-trigger ${isOpen ? 'is-open' : ''} ${value ? 'has-value' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
        onClick={() => onOpenChange?.(!isOpen)}
      >
        <span className="category-dropdown-trigger-value">{displayValue}</span>
        <ChevronIcon />
      </button>

      <AnimatedPanel
        isOpen={isOpen}
        className="dropdown-shell category-dropdown-panel province-dropdown-panel"
        role="dialog"
        aria-label={label}
      >
        <div className="dropdown-shell-header category-dropdown-panel-header">
          <strong>{label}</strong>
          <button type="button" className="dropdown-shell-close" onClick={() => onOpenChange?.(false)}>
            {closeLabel}
          </button>
        </div>
        <div className="dropdown-shell-body category-dropdown-panel-body province-dropdown-panel-body">
          {options}
        </div>
      </AnimatedPanel>
    </div>
  );
};
